import React from 'react';
import Link from 'next/link';
import { PipelineResult } from '@/types/dashboard';

interface OperationsPipelineProps {
  pipeline: PipelineResult | null;
}

export default function OperationsPipeline({ pipeline }: OperationsPipelineProps) {
  if (!pipeline) return null;

  const { orders, returns } = pipeline;

  const orderStages = [
    { label: 'New / Confirmed', count: (orders?.NEW ?? 0) + (orders?.CONFIRMED ?? 0), color: 'bg-blue-500', href: '/orders?status=CONFIRMED' },
    { label: 'Processing & In-Picking', count: orders?.PROCESSING ?? 0, color: 'bg-amber-500', href: '/orders?status=PROCESSING' },
    { label: 'Packed & Ready', count: orders?.PACKED ?? 0, color: 'bg-indigo-500', href: '/marketplaces/flipkart/packing' },
    { label: 'Shipped & In Transit', count: (orders?.SHIPPED ?? 0) + (orders?.IN_TRANSIT ?? 0) + (orders?.OUT_FOR_DELIVERY ?? 0), color: 'bg-cyan-500', href: '/orders?status=SHIPPED' },
    { label: 'Delivered (SLA Met)', count: orders?.DELIVERED ?? 0, color: 'bg-emerald-500', href: '/orders?status=DELIVERED' },
    { label: 'RTO / Cancelled', count: (orders?.RTO ?? 0) + (orders?.CANCELLED ?? 0), color: 'bg-rose-500', href: '/orders?status=CANCELLED' },
  ];

  const returnStages = [
    { label: 'Pending Gate Inward', count: returns?.PENDING ?? 0, color: 'bg-amber-400', href: '/marketplaces/flipkart/returns?status=PENDING' },
    { label: 'Received at Dock (Unverified)', count: returns?.RECEIVED ?? 0, color: 'bg-blue-500', href: '/marketplaces/flipkart/returns?status=RECEIVED' },
    { label: 'QC In Progress (Scan Stage)', count: returns?.QC_IN_PROGRESS ?? 0, color: 'bg-purple-500', href: '/marketplaces/flipkart/returns?status=QC_IN_PROGRESS' },
    { label: 'QC Completed (Restocked / Bad)', count: returns?.QC_COMPLETED ?? 0, color: 'bg-indigo-500', href: '/marketplaces/flipkart/returns?status=QC_COMPLETED' },
    { label: 'SPF Claim Decided / Closed', count: returns?.DECIDED ?? 0, color: 'bg-emerald-500', href: '/marketplaces/flipkart/returns?status=DECIDED' },
  ];

  const totalOrders = orders?.total || Math.max(orderStages.reduce((acc, s) => acc + s.count, 0), 1);
  const totalReturns = returns?.total || Math.max(returnStages.reduce((acc, s) => acc + s.count, 0), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Order Fulfillment Pipeline */}
      <div className="rounded-xl bg-white p-5 shadow-xs border border-gray-200/80" data-testid="orders-pipeline-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-blue-50 text-blue-600 text-xs font-bold">🚚</span>
            <div>
              <h3 className="text-sm font-bold text-gray-900">
                Fulfillment Velocity Pipeline
              </h3>
              <p className="text-[11px] text-gray-500">Live order processing across all stages</p>
            </div>
          </div>
          <span className="inline-flex items-center rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 text-xs font-extrabold text-blue-700">
            {orders?.total ?? 0} Total Orders
          </span>
        </div>

        <div className="space-y-3">
          {orderStages.map((stage, idx) => {
            const pct = Math.min(Math.round((stage.count / totalOrders) * 100), 100);
            return (
              <Link
                key={idx}
                href={stage.href}
                className="group block p-2 rounded-lg hover:bg-gray-50/80 transition-all border border-transparent hover:border-gray-200"
              >
                <div className="flex justify-between text-xs font-medium text-gray-700 mb-1.5">
                  <span className="group-hover:text-indigo-600 font-semibold transition-colors flex items-center gap-1">
                    <span>{stage.label}</span>
                    <span className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">↗</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-400 font-mono">{pct}%</span>
                    <span className="text-gray-900 font-bold font-mono bg-gray-100 px-2 py-0.5 rounded-md text-xs">{stage.count}</span>
                  </div>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${stage.color} transition-all duration-300 group-hover:brightness-95`}
                    style={{ width: `${stage.count > 0 ? Math.max(pct, 5) : 0}%` }}
                  />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Return & QC Pipeline */}
      <div className="rounded-xl bg-white p-5 shadow-xs border border-gray-200/80" data-testid="returns-pipeline-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-orange-50 text-orange-600 text-xs font-bold">🛡️</span>
            <div>
              <h3 className="text-sm font-bold text-gray-900">
                Returns &amp; QC Gate Lifecycle
              </h3>
              <p className="text-[11px] text-gray-500">Inward scan verification &amp; restock audit</p>
            </div>
          </div>
          <span className="inline-flex items-center rounded-full bg-orange-50 border border-orange-200 px-2.5 py-0.5 text-xs font-extrabold text-orange-700">
            {returns?.total ?? 0} Returns
          </span>
        </div>

        <div className="space-y-3">
          {returnStages.map((stage, idx) => {
            const pct = Math.min(Math.round((stage.count / totalReturns) * 100), 100);
            return (
              <Link
                key={idx}
                href={stage.href}
                className="group block p-2 rounded-lg hover:bg-gray-50/80 transition-all border border-transparent hover:border-gray-200"
              >
                <div className="flex justify-between text-xs font-medium text-gray-700 mb-1.5">
                  <span className="group-hover:text-orange-600 font-semibold transition-colors flex items-center gap-1">
                    <span>{stage.label}</span>
                    <span className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">↗</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-400 font-mono">{pct}%</span>
                    <span className="text-gray-900 font-bold font-mono bg-gray-100 px-2 py-0.5 rounded-md text-xs">{stage.count}</span>
                  </div>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${stage.color} transition-all duration-300 group-hover:brightness-95`}
                    style={{ width: `${stage.count > 0 ? Math.max(pct, 5) : 0}%` }}
                  />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

